import React, { useState, useEffect } from "react";
import { C, RFLogo } from "./theme";
import { APP_LOCALES, I18N } from "./i18n";
import { apiFetch, mapApiSession, Btn } from "./shared";
import { CODES_RESIDENCE, PLANS } from "./data";

export const AuthScreen = ({ onLogin, onRegister, residents, prestataires, locale, onLocaleChange }) => {
  const copy = I18N[locale] || I18N.fr;
  const [mode, setMode]   = useState("login");   // "login" | "register"
  const [role, setRole]   = useState("syndic");
  const [email, setEmail] = useState("");
  const [pass, setPass]   = useState("");
  const [err, setErr]     = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Register form
  const [rNom,      setRNom]      = useState("");
  const [rPrenom,   setRPrenom]   = useState("");
  const [rEmail,    setREmail]    = useState("");
  const [rPass,     setRPass]     = useState("");
  const [rPass2,    setRPass2]    = useState("");
  const [rTel,      setRTel]      = useState("");
  const [rApt,      setRApt]      = useState("");
  const [rCode,     setRCode]     = useState("");
  const [rResidence, setRResidence] = useState("");
  const [rCity, setRCity] = useState("");
  const [rServiceCat, setRServiceCat] = useState("Nettoyage");
  const [rMessage, setRMessage] = useState("");
  const [rStep,     setRStep]     = useState(1);  // 1=code, 2=infos, 3=confirm

  const LOGIN_ROLES = [
    {k:"syndic",     icon:"🔑", l:copy.roleSyndic,  sub:"Administration"},
    {k:"resident",   icon:"🏠", l:copy.roleOwner,   sub:"Espace résident"},
    {k:"prestataire",icon:"🔧", l:copy.roleProvider,sub:"Espace prestataire"},
    {k:"admin",      icon:"🛡️", l:copy.roleAdmin,   sub:"Plateforme"},
  ];
  const REGISTER_ROLES = LOGIN_ROLES.filter((item) => item.k !== "admin");
  const hints = {
    syndic:     {e:"syndic@resiflow.ma",  p:"password"},
    resident:   {e:"k.elalami@email.com", p:"1234"},
    prestataire:{e:"contact@cleanpro.ma", p:"1234"},
    admin:      {e:"admin@resiflow.ma",   p:"admin123"},
  };

  const tryLogin = async () => {
    setErr("");
    setLoading(true);

    // Demo-first auth (no external API dependency)
    try {
      if (role === "resident") {
        const r = residents.find((x) => x.email === email && x.pass === pass);
        if (r) {
          onLogin({ role:"resident", data:r, source:"demo" });
          setLoading(false);
          return;
        }
      }

      if (role === "prestataire") {
        const p = prestataires.find((x) => x.email === email && (x.pass || "1234") === pass);
        if (p) {
          onLogin({ role:"prestataire", data:p, source:"demo" });
          setLoading(false);
          return;
        }
      }

      if (role === "admin" && email === hints.admin.e && pass === hints.admin.p) {
        onLogin({ role:"admin", source:"demo", data:{ email, name:"Admin ResiFlow" } });
        setLoading(false);
        return;
      }

      if (role === "syndic" && email === hints.syndic.e && pass === hints.syndic.p) {
        onLogin({ role:"syndic", source:"demo" });
        setLoading(false);
        return;
      }

      setErr(apiError.message || "Email ou mot de passe incorrect.");
    } finally {
      setLoading(false);
    }
  };

  const checkCode = () => {
    setErr("");
    const found = CODES_RESIDENCE[rCode.toUpperCase().trim()];
    if (!found) { setErr(copy.invalidCode); return; }
    setRStep(2);
  };

  const submitRegister = async () => {
    setErr("");
    if (role === "prestataire") {
      if (!rNom || !rPrenom || !rEmail || !rResidence || !rServiceCat || !rMessage || !rPass) {
        setErr("Veuillez remplir tous les champs obligatoires.");
        return;
      }
      if (rPass !== rPass2) { setErr("Les mots de passe ne correspondent pas."); return; }
    } else {
      if (!rNom || !rPrenom || !rEmail || !rPass) { setErr("Veuillez remplir tous les champs obligatoires."); return; }
      if (rPass !== rPass2) { setErr("Les mots de passe ne correspondent pas."); return; }
    }

    setLoading(true);

    try {
      if (role === "syndic") {
        if (!rResidence) {
          setErr("Veuillez renseigner le nom de la residence.");
          setLoading(false);
          return;
        }

        const result = await apiFetch("/auth/register/syndic", {
          method: "POST",
          headers: { "X-Locale": locale },
          body: JSON.stringify({
            email: rEmail,
            password: rPass,
            firstName: rPrenom,
            lastName: rNom,
            language: locale,
            residenceName: rResidence,
            city: rCity || "Fes",
            countryCode: "MA",
            timezone: "Africa/Casablanca",
          }),
        });

        setRStep(3);
        setSuccess(result.message || "Compte syndic cree. Vous pouvez maintenant vous connecter.");
        return;
      }

      if (role === "prestataire") {
        const result = await onRegister({
          type: "provider",
          companyName: rResidence,
          firstName: rPrenom,
          lastName: rNom,
          email: rEmail,
          phone: rTel,
          serviceCategory: rServiceCat,
          description: rMessage,
          password: rPass
        });

        setRStep(3);
        setSuccess(result?.message || copy.providerSent);
        return;
      }

      if (!rApt || !rCode) {
        setErr("Le code d'invitation et l'appartement sont obligatoires.");
        return;
      }

      const result = await onRegister({
        invitationCode: rCode.toUpperCase().trim(),
        email: rEmail,
        password: rPass,
        firstName: rPrenom,
        lastName: rNom,
        apartmentLabel: rApt,
        phone: rTel,
        language: locale,
      });

      setRStep(3);
      setSuccess(result?.message || copy.ownerSubmitted);
    } catch (apiError) {
      setErr(apiError.message || "Echec de l'inscription.");
    } finally {
      setLoading(false);
    }
  };

  const resetRegister = () => {
    setRNom(""); setRPrenom(""); setREmail(""); setRPass(""); setRPass2("");
    setRTel(""); setRApt(""); setRCode(""); setRResidence(""); setRCity("");
    setRServiceCat("Nettoyage"); setRMessage("");
    setRStep(role === "syndic" ? 2 : 1); setErr(""); setSuccess("");
    setMode("login");
  };

  useEffect(() => {
    setErr("");
    setSuccess("");
    setRStep(role === "syndic" ? 2 : 1);
  }, [role]);

  const ifield = (ph, val, set, type="text") => (
    <input type={type} value={val} onChange={e => set(e.target.value)} placeholder={ph}
      className="inp"
      style={{width:"100%",padding:"11px 14px",borderRadius:12,border:"1.5px solid rgba(255,255,255,.15)",background:"rgba(255,255,255,.09)",color:"#fff",fontSize:14,marginBottom:10,fontFamily:"DM Sans"}} />
  );

  return (
    <div style={{minHeight:"100vh",background:`linear-gradient(135deg,${C.navy} 0%,#162070 100%)`,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{position:"absolute",inset:0,backgroundImage:"radial-gradient(circle at 15% 85%,rgba(14,201,164,.18) 0%,transparent 45%),radial-gradient(circle at 80% 20%,rgba(79,110,247,.15) 0%,transparent 40%)"}} />
      <div style={{position:"relative",zIndex:1,width:"100%",maxWidth:mode==="register"?520:480}}>

        {/* Logo */}
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{display:"inline-flex",marginBottom:12}}><RFLogo size={56} /></div>
          <h1 style={{color:"#fff",fontSize:32,fontFamily:"Syne",fontWeight:800,letterSpacing:"-1.5px"}}>RésiFlow</h1>
          <p style={{color:"rgba(255,255,255,.4)",fontSize:13,marginTop:3}}>{copy.tagline}</p>
        </div>

        <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",marginBottom:14,gap:12}}>
          <select
            value={locale}
            onChange={(e) => onLocaleChange(e.target.value)}
            style={{padding:"9px 12px",borderRadius:10,border:"1px solid rgba(255,255,255,.14)",background:"rgba(255,255,255,.08)",color:"#fff"}}
          >
            {APP_LOCALES.map((item) => (
              <option key={item.value} value={item.value} style={{color:"#111827"}}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        {/* Mode tabs */}
        <div style={{display:"flex",gap:4,background:"rgba(255,255,255,.07)",borderRadius:14,padding:5,marginBottom:20}}>
          {[{k:"login",l:copy.login},{k:"register",l:copy.register}].map(({k,l}) => (
            <button key={k} className="btn" onClick={() => { setMode(k); setErr(""); setRStep(1); }}
              style={{flex:1,padding:"10px",borderRadius:10,fontWeight:700,fontSize:14,
                background:mode===k?"#fff":"transparent",
                color:mode===k?C.navy:"rgba(255,255,255,.6)"}}>
              {l}
            </button>
          ))}
        </div>

        {/* ─── LOGIN ─── */}
        {mode === "login" && (
          <div className="fi">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:18}}>
              {LOGIN_ROLES.map(({k,icon,l,sub}) => (
                <div key={k} onClick={() => { setRole(k); setEmail(""); setPass(""); setErr(""); }}
                  style={{padding:"12px 8px",borderRadius:12,border:role===k?`2px solid ${C.teal}`:"2px solid rgba(255,255,255,.1)",
                    background:role===k?"rgba(14,201,164,.15)":"rgba(255,255,255,.05)",cursor:"pointer",textAlign:"center",transition:"all .15s"}}>
                  <div style={{fontSize:22,marginBottom:5}}>{icon}</div>
                  <p style={{color:role===k?"#fff":"rgba(255,255,255,.5)",fontWeight:700,fontSize:12,fontFamily:"Syne"}}>{l}</p>
                  {role===k && <div style={{width:16,height:2,background:C.teal,borderRadius:2,margin:"5px auto 0"}} />}
                </div>
              ))}
            </div>

            <div style={{background:"rgba(255,255,255,.07)",backdropFilter:"blur(24px)",borderRadius:20,padding:26,border:"1px solid rgba(255,255,255,.1)"}}>
              <div style={{background:"rgba(14,201,164,.12)",borderRadius:9,padding:"8px 13px",marginBottom:16,display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:14}}>💡</span>
                <p style={{color:"rgba(255,255,255,.7)",fontSize:12}}><strong style={{color:"#fff"}}>Démo</strong> — {hints[role].e} / {hints[role].p}</p>
              </div>
              <label style={{display:"block",fontSize:10,fontWeight:700,color:"rgba(255,255,255,.4)",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>{copy.email}</label>
              {ifield(hints[role].e, email, setEmail, "email")}
              <label style={{display:"block",fontSize:10,fontWeight:700,color:"rgba(255,255,255,.4)",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>{copy.password}</label>
              {ifield("••••••••", pass, setPass, "password")}
              {err && <div style={{background:"rgba(255,80,80,.12)",border:"1px solid rgba(255,80,80,.3)",borderRadius:9,padding:"8px 12px",marginBottom:12,color:"#ff9090",fontSize:13}}>⚠️ {err}</div>}
              <button className="btn" onClick={tryLogin}
                style={{width:"100%",padding:"13px",borderRadius:12,fontWeight:700,fontSize:15,
                  background:`linear-gradient(135deg,${C.teal},#0ba882)`,color:"#fff",boxShadow:"0 5px 20px rgba(14,201,164,.38)"}} disabled={loading}>
                {loading ? copy.connecting : copy.access}
              </button>
              <p style={{textAlign:"center",marginTop:12,fontSize:12,color:"rgba(255,255,255,.35)"}}>
                {copy.noAccount}{" "}
                <span style={{color:C.teal,cursor:"pointer",fontWeight:600}} onClick={() => setMode("register")}>{copy.register}</span>
              </p>
            </div>
          </div>
        )}

        {/* ─── REGISTER ─── */}
        {mode === "register" && (
          <div className="fi">
            <div style={{background:"rgba(255,255,255,.07)",backdropFilter:"blur(24px)",borderRadius:20,padding:28,border:"1px solid rgba(255,255,255,.1)"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:18}}>
                {REGISTER_ROLES.map(({k,icon,l}) => (
                  <div key={k} onClick={() => setRole(k)}
                    style={{padding:"12px 8px",borderRadius:12,border:role===k?`2px solid ${C.teal}`:"2px solid rgba(255,255,255,.1)",
                      background:role===k?"rgba(14,201,164,.15)":"rgba(255,255,255,.05)",cursor:"pointer",textAlign:"center"}}>
                    <div style={{fontSize:22,marginBottom:5}}>{icon}</div>
                    <p style={{color:role===k?"#fff":"rgba(255,255,255,.5)",fontWeight:700,fontSize:12,fontFamily:"Syne"}}>{l}</p>
                  </div>
                ))}
              </div>

              {/* Step indicator */}
              {role !== "syndic" && role !== "prestataire" && <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginBottom:24}}>
                {[{n:1,l:"Code"},{n:2,l:"Infos"},{n:3,l:"Confirmation"}].map((s,i) => (
                  <React.Fragment key={s.n}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                      <div style={{width:30,height:30,borderRadius:"50%",
                        background:rStep>=s.n?C.teal:"rgba(255,255,255,.15)",
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:13,fontWeight:700,color:rStep>=s.n?"#fff":"rgba(255,255,255,.4)",
                        transition:"all .2s"}}>
                        {rStep > s.n ? "✓" : s.n}
                      </div>
                      <span style={{fontSize:10,color:rStep>=s.n?"rgba(255,255,255,.7)":"rgba(255,255,255,.3)",fontWeight:600}}>{s.l}</span>
                    </div>
                    {i < 2 && <div style={{width:40,height:2,background:rStep>s.n?C.teal:"rgba(255,255,255,.15)",borderRadius:2,marginBottom:16,transition:"all .3s"}} />}
                  </React.Fragment>
                ))}
              </div>}

              {/* STEP 1 – Code résidence */}
              {role === "resident" && rStep === 1 && (
                <div className="fi">
                  <div style={{textAlign:"center",marginBottom:20}}>
                    <p style={{fontSize:16,fontWeight:700,color:"#fff",marginBottom:6}}>{copy.ownerRegisterTitle}</p>
                    <p style={{fontSize:13,color:"rgba(255,255,255,.45)"}}>{copy.ownerRegisterDesc}</p>
                  </div>
                  <label style={{display:"block",fontSize:10,fontWeight:700,color:"rgba(255,255,255,.4)",marginBottom:6,textTransform:"uppercase",letterSpacing:".5px"}}>Code de résidence</label>
                  <input className="inp" value={rCode} onChange={e => setRCode(e.target.value.toUpperCase())}
                    placeholder="Ex: LES-ORG-2024" onKeyDown={e => e.key==="Enter"&&checkCode()}
                    style={{width:"100%",padding:"13px 16px",borderRadius:12,border:"1.5px solid rgba(255,255,255,.2)",background:"rgba(255,255,255,.1)",color:"#fff",fontSize:16,fontWeight:700,letterSpacing:"2px",textAlign:"center",fontFamily:"DM Sans",marginBottom:10}} />
                  {err && <div style={{background:"rgba(255,80,80,.12)",border:"1px solid rgba(255,80,80,.3)",borderRadius:9,padding:"8px 12px",marginBottom:12,color:"#ff9090",fontSize:13}}>⚠️ {err}</div>}
                  <button className="btn" onClick={checkCode}
                    style={{width:"100%",padding:"12px",borderRadius:12,fontWeight:700,fontSize:15,
                      background:`linear-gradient(135deg,${C.teal},#0ba882)`,color:"#fff"}}>
                    {copy.verifyCode}
                  </button>
                  <div style={{marginTop:18,background:"rgba(255,255,255,.06)",borderRadius:12,padding:"12px 16px"}}>
                    <p style={{color:"rgba(255,255,255,.5)",fontSize:12,marginBottom:6,fontWeight:600}}>💡 Codes de démo disponibles :</p>
                    {Object.entries(CODES_RESIDENCE).map(([code, info]) => (
                      <div key={code} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 0"}}>
                        <span style={{fontFamily:"monospace",color:C.teal,fontWeight:700,fontSize:13}}>{code}</span>
                        <span style={{color:"rgba(255,255,255,.4)",fontSize:12}}>{info.nom}</span>
                      </div>
                    ))}
                  </div>
                  <p style={{textAlign:"center",marginTop:14,fontSize:12,color:"rgba(255,255,255,.35)"}}>
                    {copy.alreadyRegistered}{" "}
                    <span style={{color:C.teal,cursor:"pointer",fontWeight:600}} onClick={() => setMode("login")}>{copy.login}</span>
                  </p>
                </div>
              )}

              {/* STEP 2 – Informations personnelles */}
              {role === "resident" && rStep === 2 && (
                <div className="fi">
                  <div style={{background:"rgba(14,201,164,.12)",borderRadius:10,padding:"10px 14px",marginBottom:18,display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:18}}>✅</span>
                    <div>
                      <p style={{color:"#fff",fontWeight:700,fontSize:13}}>{CODES_RESIDENCE[rCode]?.nom}</p>
                      <p style={{color:"rgba(255,255,255,.5)",fontSize:12}}>{copy.validCode}</p>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    {ifield(copy.fullName, rNom, setRNom)}
                    {ifield(copy.firstName, rPrenom, setRPrenom)}
                  </div>
                  {ifield(`${copy.email} *`, rEmail, setREmail, "email")}
                  {ifield(copy.phone, rTel, setRTel, "tel")}
                  {ifield(copy.apartment, rApt, setRApt)}
                  {ifield(`${copy.password} *`, rPass, setRPass, "password")}
                  {ifield(copy.confirmPassword, rPass2, setRPass2, "password")}
                  {err && <div style={{background:"rgba(255,80,80,.12)",border:"1px solid rgba(255,80,80,.3)",borderRadius:9,padding:"8px 12px",marginBottom:10,color:"#ff9090",fontSize:13}}>⚠️ {err}</div>}
                  <div style={{display:"flex",gap:10}}>
                    <button className="btn" onClick={() => { setRStep(1); setErr(""); }}
                      style={{padding:"11px 18px",borderRadius:12,fontWeight:600,fontSize:14,background:"rgba(255,255,255,.1)",color:"rgba(255,255,255,.7)"}}>
                      ← Retour
                    </button>
                    <button className="btn" onClick={submitRegister} disabled={loading}
                      style={{flex:1,padding:"11px",borderRadius:12,fontWeight:700,fontSize:15,
                        background:`linear-gradient(135deg,${C.teal},#0ba882)`,color:"#fff"}}>
                      {loading ? copy.sending : copy.sendRequest}
                    </button>
                  </div>
                </div>
              )}

              {role === "syndic" && rStep === 2 && (
                <div className="fi">
                  <div style={{textAlign:"center",marginBottom:20}}>
                    <p style={{fontSize:16,fontWeight:700,color:"#fff",marginBottom:6}}>{copy.syndicRegisterTitle}</p>
                    <p style={{fontSize:13,color:"rgba(255,255,255,.45)"}}>{copy.syndicRegisterDesc}</p>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    {ifield(copy.fullName, rNom, setRNom)}
                    {ifield(copy.firstName, rPrenom, setRPrenom)}
                  </div>
                  {ifield(`${copy.email} *`, rEmail, setREmail, "email")}
                  {ifield(copy.residenceName, rResidence, setRResidence)}
                  {ifield(copy.city, rCity, setRCity)}
                  {ifield(`${copy.password} *`, rPass, setRPass, "password")}
                  {ifield(copy.confirmPassword, rPass2, setRPass2, "password")}
                  {err && <div style={{background:"rgba(255,80,80,.12)",border:"1px solid rgba(255,80,80,.3)",borderRadius:9,padding:"8px 12px",marginBottom:10,color:"#ff9090",fontSize:13}}>⚠️ {err}</div>}
                  <button className="btn" onClick={submitRegister} disabled={loading}
                    style={{width:"100%",padding:"11px",borderRadius:12,fontWeight:700,fontSize:15,
                      background:`linear-gradient(135deg,${C.teal},#0ba882)`,color:"#fff"}}>
                    {loading ? copy.creating : copy.createSyndic}
                  </button>
                </div>
              )}

              {role === "prestataire" && (
                <div className="fi">
                  <div style={{textAlign:"center",marginBottom:20}}>
                    <div style={{fontSize:44,marginBottom:12}}>🔧</div>
                    <h3 style={{color:"#fff",fontSize:18,fontWeight:800,marginBottom:10}}>{copy.providerRegisterTitle}</h3>
                    <p style={{color:"rgba(255,255,255,.55)",fontSize:14,lineHeight:1.7}}>{copy.providerRegisterDesc}</p>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    {ifield(copy.fullName, rNom, setRNom)}
                    {ifield(copy.firstName, rPrenom, setRPrenom)}
                  </div>
                  {ifield(copy.providerCompany, rResidence, setRResidence)}
                  {ifield(`${copy.email} *`, rEmail, setREmail, "email")}
                  {ifield(copy.phone, rTel, setRTel, "tel")}
                  {ifield(copy.providerCategory, rServiceCat, setRServiceCat)}
                  {ifield(`${copy.password} *`, rPass, setRPass, "password")}
                  {ifield(copy.confirmPassword, rPass2, setRPass2, "password")}
                  <textarea className="inp" rows={4} value={rMessage} onChange={e => setRMessage(e.target.value)} placeholder={copy.providerMessage}
                    style={{width:"100%",padding:"11px 14px",borderRadius:12,border:"1.5px solid rgba(255,255,255,.15)",background:"rgba(255,255,255,.09)",color:"#fff",fontSize:14,marginBottom:10,fontFamily:"DM Sans",resize:"vertical"}} />
                  {err && <div style={{background:"rgba(255,80,80,.12)",border:"1px solid rgba(255,80,80,.3)",borderRadius:9,padding:"8px 12px",marginBottom:10,color:"#ff9090",fontSize:13}}>⚠️ {err}</div>}
                  <button className="btn" onClick={submitRegister} disabled={loading}
                    style={{width:"100%",padding:"11px",borderRadius:12,fontWeight:700,fontSize:15,
                      background:`linear-gradient(135deg,${C.teal},#0ba882)`,color:"#fff"}}>
                    {loading ? copy.sending : copy.sendRequest}
                  </button>
                </div>
              )}

              {/* STEP 3 – Confirmation */}
              {rStep === 3 && (
                <div className="fi" style={{textAlign:"center"}}>
                  <div style={{fontSize:52,marginBottom:16}}>🎉</div>
                  <h3 style={{color:"#fff",fontSize:18,fontWeight:800,marginBottom:10}}>{copy.sendRequest}</h3>
                  <p style={{color:"rgba(255,255,255,.55)",fontSize:14,lineHeight:1.7,marginBottom:24}}>{success}</p>
                  <div style={{background:"rgba(14,201,164,.1)",borderRadius:12,padding:"14px 18px",marginBottom:22,textAlign:"left"}}>
                    <p style={{color:"rgba(255,255,255,.6)",fontSize:13,fontWeight:600,marginBottom:8}}>{copy.nextSteps}</p>
                    {["Votre syndic reçoit une notification de votre demande","Il vérifie vos informations et votre appartement","Vous recevrez un email de confirmation avec vos accès","Vous pourrez alors vous connecter au portail RésiFlow"].map((s,i) => (
                      <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:6}}>
                        <span style={{width:20,height:20,borderRadius:"50%",background:C.teal,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0}}>{i+1}</span>
                        <p style={{color:"rgba(255,255,255,.55)",fontSize:12,lineHeight:1.5}}>{s}</p>
                      </div>
                    ))}
                  </div>
                  <button className="btn" onClick={resetRegister}
                    style={{width:"100%",padding:"12px",borderRadius:12,fontWeight:700,fontSize:14,
                      background:"rgba(255,255,255,.1)",color:"rgba(255,255,255,.7)"}}>
                    {copy.backToLogin}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <p style={{textAlign:"center",color:"rgba(255,255,255,.18)",fontSize:11,marginTop:16}}>© 2024 RésiFlow · resiflow.ma · RGPD compliant</p>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════
   PORTAIL RÉSIDENT
══════════════════════════════════════ */
